import { useEffect } from 'react';
import mixpanel from 'mixpanel-browser';

// The Pet Parent Hub Mixpanel project token. Read from the environment so staging/branch
// deploys and local dev can point at a separate test project; falls back to the production
// token so prod is unaffected if the var is unset. Keep in sync with the init calls.
export const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || '1753030ad5be742490513cb47e277e47';

// Initialised here rather than in the Hub so every surface using these helpers is
// covered — including the public lesson pages, which render outside the Hub tree
// and were therefore firing no events at all.
//
// Auto pageview stays off: it can only emit the un-renamable `$mp_web_page_view`
// ("[Auto] Page View"). Screens fire an explicit `page_view` via usePageView.
mixpanel.init(MIXPANEL_TOKEN, {
  debug: true,
  track_pageview: false,
});

/**
 * Attach subsequent events to a pet parent's Mixpanel profile.
 *
 * Always the numeric pet parent id — the same value the Hub identifies with — so
 * public-page and in-hub events land on ONE profile. Email is deliberately not the
 * identity: it is mutable here (PetParentEmail / set_primary_email) so it would
 * fragment profiles, and it would have to travel in a URL to reach a public page.
 * The Hub sets it as a profile property instead.
 */
export function identifyPetParent(petParentId: string | number): void {
  mixpanel.identify(String(petParentId));
}

// `from_where` identifies which surface an upsell / contact CTA was clicked from. All upsell
// banners in the Hub funnel through `extend_membership_click` with one of these values so the
// data lines up with the tracking spec.
export type FromWhere = 'home' | 'messages' | 'settings' | 'albums';

/**
 * Thin wrapper around mixpanel.track. Preserves the callback-before-navigate pattern: when a
 * click both tracks an event and navigates (or opens a new tab), pass the navigation as
 * `callback` so Mixpanel fires the request before the page unloads and the event isn't lost.
 */
export function track(
  event: string,
  props?: Record<string, unknown>,
  callback?: () => void,
): void {
  mixpanel.track(event, props, callback ? () => callback() : undefined);
}

/**
 * Fire a `page_view` event once per screen mount. Uses mixpanel.track_pageview with a custom
 * event_name so we get the spec's exact `page_view` name plus Mixpanel's rich default page
 * properties (url path, title, referrer, campaign/UTM params). The automatic pageview is
 * disabled at init (it can only emit the un-renamable `$mp_web_page_view`).
 *
 * Pass `deps` when the page properties become available asynchronously (e.g. an album's name
 * after the query resolves) so the event fires once the data is ready, and `enabled` to hold
 * the event until the data is present (avoids firing a property-less page_view while loading).
 */
export function usePageView(
  props?: Record<string, unknown>,
  deps: unknown[] = [],
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    mixpanel.track_pageview(props || {}, { event_name: 'page_view' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
